import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ToggleInputSection from '@/components/ui/ToggleInputSection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import Typography from '@/components/ui/typography';
import CodeGuardIframe from '@/components/ui/CodeGuardIframe';
import MultiSelectChips from '@/components/ui/MultiSelectChips';
import { ArrowLeft } from 'lucide-react';
import usePayers from '@/hooks/usePayers';
import useProviders from '@/hooks/useProviders';
import { RouteConstants } from '@/constants/route-constant';

interface IframeData {
  icd10Enabled: boolean;
  cptEnabled: boolean;
  modifiersEnabled: boolean;
  noteEnabled: boolean;
  icd10Codes: string;
  cptCodes: string;
  modifierCodes: string;
  noteText: string;
  // Advanced fields
  payerEnabled: boolean;
  dateOfServiceEnabled: boolean;
  feeEnabled: boolean;
  claimDateEnabled: boolean;
  renderingProviderEnabled: boolean;
  billingProviderEnabled: boolean;
  supervisingProviderEnabled: boolean;
  referringProviderEnabled: boolean;
  patientStatusEnabled: boolean;
  unitsEnabled: boolean;
  payerValue: string;
  dateOfServiceValue: string;
  feeValue: string;
  claimDateValue: string;
  renderingProviderValue: string;
  billingProviderValue: string;
  supervisingProviderValue: string;
  referringProviderValue: string;
  patientStatusValue: string;
  unitsValue: string;
}

interface IframeMessage {
  type: string;
  payload?: IframeData;
}

const Check: React.FC = () => {
  const navigate = useNavigate();
  const { payers: payersData } = usePayers();
  const { billingProviders, renderingProviders, supervisingProviders } =
    useProviders();

  const [icd10Enabled, setIcd10Enabled] = useState(true);
  const [cptEnabled, setCptEnabled] = useState(true);
  const [modifiersEnabled, setModifiersEnabled] = useState(true);
  const [noteEnabled, setNoteEnabled] = useState(true);
  const [icd10Codes, setIcd10Codes] = useState('');
  const [cptCodes, setCptCodes] = useState('');
  const [modifiers, setModifiers] = useState('');
  const [noteText, setNoteText] = useState('');

  // Advanced fields state
  const [advancedEnabled, setAdvancedEnabled] = useState(false);
  const [payerEnabled, setPayerEnabled] = useState(false);
  const [dateOfServiceEnabled, setDateOfServiceEnabled] = useState(false);
  const [feeEnabled, setFeeEnabled] = useState(false);
  const [claimDateEnabled, setClaimDateEnabled] = useState(false);
  const [unitsEnabled, setUnitsEnabled] = useState(false);
  const [payerValue, setPayerValue] = useState<string[]>([]);
  const [dateOfServiceValue, setDateOfServiceValue] = useState('');
  const [feeValue, setFeeValue] = useState('');
  const [claimDateValue, setClaimDateValue] = useState('');
  const [unitsValue, setUnitsValue] = useState('');

  // Provider states
  const [billingProviderEnabled, setBillingProviderEnabled] = useState(false);
  const [renderingProviderEnabled, setRenderingProviderEnabled] =
    useState(false);
  const [supervisingProviderEnabled, setSupervisingProviderEnabled] =
    useState(false);
  const [billingProviderValue, setBillingProviderValue] = useState<string[]>(
    [],
  );
  const [renderingProviderValue, setRenderingProviderValue] = useState<
    string[]
  >([]);
  const [supervisingProviderValue, setSupervisingProviderValue] = useState<
    string[]
  >([]);

  // Patient Status states
  const [patientStatusEnabled, setPatientStatusEnabled] = useState(false);
  const [patientStatusValue, setPatientStatusValue] = useState('Is New');

  // Transform payers data for MultiSelectChips component
  const payerOptions = payersData
    ?.map((payer) => ({
      value: payer.athenaPayerId,
      label: payer.name,
    }))
    ?.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  // Transform providers data for MultiSelectChips component
  const billingProviderOptions = billingProviders
    ?.map((provider) => ({
      value: provider.npi,
      label: `${provider.firstName} ${provider.lastName}${(provider as { suffix?: string }).suffix ? `, ${(provider as { suffix?: string }).suffix}` : ''}`,
    }))
    ?.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  const renderingProviderOptions = renderingProviders
    ?.map((provider) => ({
      value: provider.npi,
      label: `${provider.firstName} ${provider.lastName}${(provider as { suffix?: string }).suffix ? `, ${(provider as { suffix?: string }).suffix}` : ''}`,
    }))
    ?.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  const supervisingProviderOptions = supervisingProviders
    ?.map((provider) => ({
      value: provider.npi,
      label: `${provider.firstName} ${provider.lastName}${(provider as { suffix?: string }).suffix ? `, ${(provider as { suffix?: string }).suffix}` : ''}`,
    }))
    ?.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  const handleBack = () => {
    navigate(RouteConstants.ROUTE_HOME, { replace: true });
  };

  const iframeData: IframeData = {
    icd10Enabled,
    cptEnabled,
    modifiersEnabled,
    noteEnabled,
    icd10Codes: icd10Enabled ? icd10Codes : '',
    cptCodes: cptEnabled ? cptCodes : '',
    modifierCodes: modifiersEnabled ? modifiers : '',
    noteText: noteEnabled ? noteText : '',
    // Advanced fields
    payerEnabled,
    dateOfServiceEnabled,
    feeEnabled,
    claimDateEnabled,
    renderingProviderEnabled,
    billingProviderEnabled,
    supervisingProviderEnabled,
    referringProviderEnabled: false,
    patientStatusEnabled,
    unitsEnabled,
    payerValue: payerEnabled ? payerValue.join(',') : '',
    dateOfServiceValue: dateOfServiceEnabled ? dateOfServiceValue : '',
    feeValue: feeEnabled ? feeValue : '',
    claimDateValue:
      claimDateEnabled && dateOfServiceEnabled ? claimDateValue : '',
    renderingProviderValue: renderingProviderEnabled
      ? renderingProviderValue.join(',')
      : '',
    billingProviderValue: billingProviderEnabled
      ? billingProviderValue.join(',')
      : '',
    supervisingProviderValue: supervisingProviderEnabled
      ? supervisingProviderValue.join(',')
      : '',
    referringProviderValue: '',
    patientStatusValue: patientStatusEnabled ? patientStatusValue : '',
    unitsValue: unitsEnabled ? unitsValue : '',
  };

  // Function to clear all form fields
  // FIXME:need for future use
  // const clearFormFields = () => {
  //   console.log('Clearing form fields...');
  //   setIcd10Codes('');
  //   setCptCodes('');
  //   setModifiers('');
  //   setNoteText('');
  //   console.log('Form fields cleared');
  // };

  // Handle data received from iframe
  const handleDataReceived = (data: IframeMessage) => {
    console.log('Data received from iframe:', data);

    // if (data.type === 'MAIA_CLEAR_FIELDS') {
    //   console.log('MAIA_CLEAR_FIELDS detected, calling clearFormFields...');
    //   clearFormFields();
    // }
  };

  return (
    <div className="p-6 mx-auto h-screen flex flex-col">
      {/* Header */}

      {/* Main Content Area - 65/35 Layout */}
      <div className="flex gap-2 flex-1 min-h-0">
        {/* Left Side - Main Content (65%) */}
        <div
          className="w-[65%] bg-white dark:bg-gray-800 rounded-lg p-6 overflow-y-auto border border-gray-200 dark:border-gray-700 scrollbar-hide"
          style={{ borderRadius: '8px' }}
        >
          <div className="flex flex-row items-center gap-4 mb-8">
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>

            <Typography
              variant="h1"
              color="neutral1000"
              weight="bold"
              size="24"
            >
              Check your note
            </Typography>
          </div>
          <div className="mb-10">
            <Typography
              variant="h2"
              color="neutral500"
              weight="semibold"
              className="mb-3"
            >
              Information
            </Typography>
            <Typography variant="p" color="neutral700" className="mb-4">
              Select the fields and input the information to run a Maia
              CodeGuard check.
            </Typography>
            {/* <div className="space-y-2">
              <Typography variant="small" color="neutral700">
                Step 1: Select the fields you want Maia to check.
              </Typography>
              <Typography variant="small" color="neutral700">
                Step 2: Input the codes in the fields that have been selected.
              </Typography>
              <Typography variant="small" color="neutral700">
                Step 3: Paste the physician's note in the text area
              </Typography>
            </div> */}
          </div>

          {/* Toggle Input Sections */}
          <div className="flex flex-col gap-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ToggleInputSection
                label="ICD-10 Codes"
                isEnabled={icd10Enabled}
                onToggle={setIcd10Enabled}
                placeholder="Enter ICD-10 codes"
                value={icd10Codes}
                onChange={(value) =>
                  setIcd10Codes(typeof value === 'string' ? value : '')
                }
              />

              <ToggleInputSection
                label="CPT Codes"
                isEnabled={cptEnabled}
                onToggle={setCptEnabled}
                placeholder="Enter CPT codes"
                value={cptCodes}
                onChange={(value) =>
                  setCptCodes(typeof value === 'string' ? value : '')
                }
              />

              <ToggleInputSection
                label="Modifiers"
                isEnabled={modifiersEnabled}
                onToggle={setModifiersEnabled}
                placeholder="Enter modifiers"
                value={modifiers}
                onChange={(value) =>
                  setModifiers(typeof value === 'string' ? value : '')
                }
              />
            </div>

            <ToggleInputSection
              label="Note"
              isEnabled={noteEnabled}
              onToggle={setNoteEnabled}
              showInput={false}
            />
          </div>

          {/* Advanced Section */}
          <div className="mb-8">
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setAdvancedEnabled(!advancedEnabled)}
                className="text-sm font-medium text-gray-700 hover:text-gray-900 cursor-pointer transition-colors"
              >
                Advanced
              </button>
            </div>

            {advancedEnabled && (
              <div className="space-y-4">
                <Typography
                  variant="small"
                  color="neutral500"
                  weight="semibold"
                  className="mb-3"
                >
                  Select additional fields
                </Typography>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ToggleInputSection
                    label="Date of Service"
                    isEnabled={dateOfServiceEnabled}
                    onToggle={(enabled) => {
                      setDateOfServiceEnabled(enabled);
                      // Auto-enable claim date when date of service is enabled
                      if (enabled) {
                        setClaimDateEnabled(true);
                      } else {
                        // Auto-disable claim date when date of service is disabled
                        setClaimDateEnabled(false);
                        setClaimDateValue(''); // Clear the claim date value
                      }
                    }}
                    placeholder="Select date"
                    value={dateOfServiceValue}
                    onChange={(value) =>
                      setDateOfServiceValue(
                        typeof value === 'string' ? value : '',
                      )
                    }
                    inputLabel="Date"
                    inputType="date"
                  />
                  <ToggleInputSection
                    label="Payer"
                    isEnabled={payerEnabled}
                    onToggle={setPayerEnabled}
                    inputLabel="Names"
                    value={payerValue}
                    onChange={(value) =>
                      setPayerValue(Array.isArray(value) ? value : [])
                    }
                  >
                    <MultiSelectChips
                      placeholder="Select payers"
                      options={payerOptions}
                      selectedValues={payerValue}
                      onSelectionChange={setPayerValue}
                      className="w-full"
                    />
                  </ToggleInputSection>

                  {/* Claim Date - Only show when Date of Service is enabled */}
                  {dateOfServiceEnabled && (
                    <ToggleInputSection
                      label="Claim Date"
                      isEnabled={claimDateEnabled}
                      onToggle={setClaimDateEnabled}
                      placeholder="Select date"
                      value={claimDateValue}
                      onChange={(value) =>
                        setClaimDateValue(
                          typeof value === 'string' ? value : '',
                        )
                      }
                      inputLabel="Date"
                      inputType="date"
                    />
                  )}

                  <ToggleInputSection
                    label="Fee"
                    isEnabled={feeEnabled}
                    onToggle={setFeeEnabled}
                    placeholder="0.00"
                    value={feeValue}
                    onChange={(value) =>
                      setFeeValue(typeof value === 'string' ? value : '')
                    }
                    inputLabel="Amount"
                    inputType="number"
                    inputProps={{
                      step: '0.01',
                      min: '0',
                      pattern: '[0-9]+([.][0-9]{1,2})?',
                    }}
                  />

                  <ToggleInputSection
                    label="Billing Provider"
                    isEnabled={billingProviderEnabled}
                    onToggle={setBillingProviderEnabled}
                    inputLabel="Names"
                    value={billingProviderValue}
                    onChange={(value) =>
                      setBillingProviderValue(Array.isArray(value) ? value : [])
                    }
                  >
                    <MultiSelectChips
                      placeholder="Select billing providers"
                      options={billingProviderOptions}
                      selectedValues={billingProviderValue}
                      onSelectionChange={setBillingProviderValue}
                      className="w-full"
                    />
                  </ToggleInputSection>

                  <ToggleInputSection
                    label="Rendering Provider"
                    isEnabled={renderingProviderEnabled}
                    onToggle={setRenderingProviderEnabled}
                    inputLabel="Names"
                    value={renderingProviderValue}
                    onChange={(value) =>
                      setRenderingProviderValue(
                        Array.isArray(value) ? value : [],
                      )
                    }
                  >
                    <MultiSelectChips
                      placeholder="Select rendering providers"
                      options={renderingProviderOptions}
                      selectedValues={renderingProviderValue}
                      onSelectionChange={setRenderingProviderValue}
                      className="w-full"
                    />
                  </ToggleInputSection>

                  <ToggleInputSection
                    label="Supervising Provider"
                    isEnabled={supervisingProviderEnabled}
                    onToggle={setSupervisingProviderEnabled}
                    inputLabel="Names"
                    value={supervisingProviderValue}
                    onChange={(value) =>
                      setSupervisingProviderValue(
                        Array.isArray(value) ? value : [],
                      )
                    }
                  >
                    <MultiSelectChips
                      placeholder="Select supervising providers"
                      options={supervisingProviderOptions}
                      selectedValues={supervisingProviderValue}
                      onSelectionChange={setSupervisingProviderValue}
                      className="w-full"
                    />
                  </ToggleInputSection>

                  <ToggleInputSection
                    label="Patient Status"
                    isEnabled={patientStatusEnabled}
                    onToggle={setPatientStatusEnabled}
                    inputLabel="Status"
                    value={patientStatusValue}
                    onChange={(value) =>
                      setPatientStatusValue(
                        typeof value === 'string' ? value : 'Is New',
                      )
                    }
                    inputType="select"
                    selectOptions={[
                      { value: 'Is New', label: 'New' },
                      { value: 'Is Not New', label: 'Established Patient' },
                    ]}
                  />

                  <ToggleInputSection
                    label="Units"
                    isEnabled={unitsEnabled}
                    onToggle={setUnitsEnabled}
                    placeholder="Enter units"
                    value={unitsValue}
                    onChange={(value) =>
                      setUnitsValue(typeof value === 'string' ? value : '')
                    }
                    inputLabel=""
                  />
                </div>
              </div>
            )}
          </div>

          {/* Text Area */}
          {noteEnabled && (
            <div>
              <Textarea
                placeholder="Paste note here..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className={`min-h-[300px] resize-none text-sm bg-neutral-50 overflow-y-auto scrollbar-hide`}
              />
            </div>
          )}
        </div>

        {/* Right Side - CodeGuard Sidepanel (35%) */}
        <div
          className="w-[35%] h-full border border-gray-200 dark:border-gray-700"
          style={{ borderRadius: '8px' }}
        >
          <CodeGuardIframe
            data={iframeData}
            onDataReceived={handleDataReceived}
          />
        </div>
      </div>
    </div>
  );
};

export default Check;
